import { Button } from "@nwga/ui/components/button";
import { Spinner } from "@nwga/ui/components/spinner";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2Icon } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { z } from "zod";
import { AuthCard } from "../components/auth-card.tsx";
import { env } from "../env.ts";
import { authClient } from "../lib/auth.ts";

/**
 * Where the verification link lands.
 *
 * Better Auth verifies the token server-side and redirects here with either a
 * success or an `error` parameter, so this page reports an outcome rather than
 * performing one. The work it does do is refresh the session cache — the
 * account's `emailVerified` just changed, and every screen that gates on it is
 * now holding a stale answer.
 */
export const Route = createFileRoute("/verify-email")({
	validateSearch: z.object({
		error: z.string().optional(),
		email: z.string().optional(),
	}),
	component: VerifyEmailPage,
});

function VerifyEmailPage() {
	const { error, email } = Route.useSearch();
	const queryClient = useQueryClient();
	const [settled, setSettled] = React.useState(false);
	const [resending, setResending] = React.useState(false);

	React.useEffect(() => {
		if (error !== undefined) {
			setSettled(true);
			return;
		}

		void queryClient.invalidateQueries().then(() => setSettled(true));
	}, [error, queryClient]);

	const resend = async () => {
		if (email === undefined) return;
		setResending(true);

		await authClient.sendVerificationEmail({
			email,
			callbackURL: `${env.webUrl}/verify-email`,
		});

		setResending(false);
		toast.success("Sent", {
			description: "Check your inbox for a fresh link.",
		});
	};

	if (!settled) {
		return (
			<AuthCard title="Confirming your email…">
				<div className="flex justify-center py-4">
					<Spinner className="size-6" />
				</div>
			</AuthCard>
		);
	}

	if (error !== undefined) {
		return (
			<AuthCard
				title="That link has expired"
				description="Verification links last one hour and can only be used once."
			>
				<div className="space-y-3">
					{email === undefined ? (
						// Without the address there is nothing to resend to; signing in
						// triggers a fresh verification email anyway.
						<Button asChild className="w-full">
							<Link to="/sign-in">Sign in to get a new link</Link>
						</Button>
					) : (
						<Button
							className="w-full"
							disabled={resending}
							onClick={() => void resend()}
						>
							{resending ? "Sending…" : "Send a new link"}
						</Button>
					)}
				</div>
			</AuthCard>
		);
	}

	return (
		<AuthCard
			title="Email confirmed"
			description="Your account is ready. Register your business to start posting openings."
		>
			<div className="space-y-4">
				<div className="flex items-center gap-3 rounded-lg border border-dashed p-4">
					<CheckCircle2Icon
						className="size-5 shrink-0 text-muted-foreground"
						aria-hidden="true"
					/>
					<p className="text-muted-foreground text-sm">
						A member of the Joint Development Authority reviews each business,
						usually within two business days.
					</p>
				</div>

				<Button asChild className="w-full">
					<Link to="/businesses/register">Register your business</Link>
				</Button>
				<Button asChild variant="outline" className="w-full">
					<Link to="/jobs">Browse the board instead</Link>
				</Button>
			</div>
		</AuthCard>
	);
}
